import type { ReactElement } from "react";
import { memo, useRef, useState } from "react";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import {
    IS_LEGACY_USER_PENDING_ACTIVATION,
    USER_CLOSED_AGENT_ACTIVATION,
    USER_COMPLETED_ACTIVATION,
} from "app/pages/sales-intelligence/modules/onboarding";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import OnboardingDialog from "../components/OnboardingDialog/OnboardingDialog";
import OnboardingModalContent from "../components/OnboardingModalContent/OnboardingModalContent";
import { useSubmitActivationAgentForm } from "../query/mutations/useSubmitActivationAgentForm";
import { useOnboardingInitQuery } from "../query/queries/useOnboardingInitQuery";
import { useOnboardingSignalsInfoQuery } from "../query/queries/useOnboardingSignalsInfoQuery";
import activationAgentTrackingService from "../services/tracking/trackingService";
import type { IActivationAgentFormDTO } from "../types/form-value";
import { OnboardingModalWindowType } from "../types/modal-window-type";

import OnboardingContentContainer from "./OnboardingContentContainer";
import OnboardingInfoModalContainer from "./OnboardingInfoModalContainer";
import OnboardingLeftPartContainer from "./OnboardingLeftPartContainer";
import { StyledOnboardingContentWrapper, StyledOnboardingLeftPartWrapper } from "./styles";

const OnboardingContainer = (): ReactElement => {
    const formDtoRef = useRef<IActivationAgentFormDTO>(null);
    const [modalWindowType, setModalWindowType] = useState<OnboardingModalWindowType | null>(
        OnboardingModalWindowType.Onboarding,
    );

    const isLegacyUser = !!PreferencesService.get(IS_LEGACY_USER_PENDING_ACTIVATION);

    const isOpenOnboarding = modalWindowType === OnboardingModalWindowType.Onboarding;
    const isOpenInfoPopup = modalWindowType === OnboardingModalWindowType.Info;

    const {
        data: initialInfoData,
        isFetching: isInfoDataFetching,
        isError: isInfoDataError,
    } = useOnboardingInitQuery();
    const {
        data: signalsInfoData,
        isFetching: isSignalsInfoFetching,
        isError: isSignalsInfoError,
    } = useOnboardingSignalsInfoQuery(!isLegacyUser);

    const { mutateAsync } = useSubmitActivationAgentForm();

    const isError = isInfoDataError || isSignalsInfoError;

    const closeOnboardingModals = (): void => {
        setModalWindowType(null);
    };

    const handleSubmitForm = (formDto: IActivationAgentFormDTO) => {
        mutateAsync(formDto)
            .then(() => {
                activationAgentTrackingService.trackSubmit(String(formDto.jobTitle), formDto.targetCompanyTypes);

                PreferencesService.add({
                    [USER_COMPLETED_ACTIVATION]: true,
                }).catch((err) => {
                    Sentry.error(`Preferences service: error adding preference (${USER_COMPLETED_ACTIVATION})`, err);
                });

                if (isLegacyUser) {
                    setModalWindowType(OnboardingModalWindowType.Info);
                    return;
                }

                closeOnboardingModals();

                if (isTraditionalTrialUser(swSettings)) {
                    void siNavigator.goToAdvancedSearchPage({ location: "replace" });
                }
            })
            .catch((err) => {
                Sentry.error(`Agent Setup: failed form submit`, err);
            });
    };

    const handleCloseOnboarding = (): void => {
        closeOnboardingModals();

        const formDto: IActivationAgentFormDTO | null = formDtoRef.current;

        if (formDto) {
            activationAgentTrackingService.trackClose(String(formDto.jobTitle), formDto.targetCompanyTypes);

            mutateAsync(formDto).catch((err) => {
                Sentry.error(`Agent Setup: failed form submit (handleCloseOnboarding)`, err);
            });
        }

        PreferencesService.add({
            [USER_CLOSED_AGENT_ACTIVATION]: true,
        }).catch((err) => {
            Sentry.error(`Preferences service: error adding preference (${USER_CLOSED_AGENT_ACTIVATION})`, err);
        });
    };

    const handleFormChange = (formDto: IActivationAgentFormDTO) => {
        formDtoRef.current = formDto;
    };

    const handleCloseInfoPopup = () => {
        closeOnboardingModals();

        if (isTraditionalTrialUser(swSettings)) {
            void siNavigator.goToAdvancedSearchPage({ location: "replace" });
        }
    };

    return (
        <>
            <OnboardingDialog
                isOpen={isOpenOnboarding}
                variant="wizard"
                dataAutomation="onboarding-modal"
                onClose={isLegacyUser ? handleCloseOnboarding : undefined}
            >
                <OnboardingModalContent isError={isError}>
                    <StyledOnboardingLeftPartWrapper>
                        <OnboardingLeftPartContainer />
                    </StyledOnboardingLeftPartWrapper>

                    <StyledOnboardingContentWrapper>
                        <OnboardingContentContainer
                            isInfoDataFetching={isInfoDataFetching}
                            isSignalsInfoFetching={isSignalsInfoFetching}
                            initialInfoData={initialInfoData}
                            signalsInfoData={signalsInfoData}
                            onFormChange={handleFormChange}
                            onSubmit={handleSubmitForm}
                        />
                    </StyledOnboardingContentWrapper>
                </OnboardingModalContent>
            </OnboardingDialog>
            <OnboardingInfoModalContainer
                isOpen={isOpenInfoPopup}
                onActionClick={handleCloseInfoPopup}
            />
        </>
    );
};

export default memo(OnboardingContainer);
